import {
  PopStoreExtendedSubscriptionLevel,
  PopStoreExtendedUserAccessLevel,
  PopStoreUserAccessLevel,
} from '@ps/models/lib/creator-subscription';

import { isPopStoreExtendedUserAccessPermitted } from '../../../subscription/configuration/functions/access-control';

/** Access levels that gate nothing — everyone sees the clip. */
const UNGATED_ACCESS_LEVELS = new Set(['all', 'ALL']);

/**
 * Whether a clip's access level gates it (subscriber / fan / vip — anything but
 * "all"). A PURE derivation from the primitive `accessLevel`, computed at RENDER
 * (the cell reads it) — never pre-baked into the view model. This is
 * VIEWER-AGNOSTIC (is the clip gated at all); {@link isClipLockedForViewer}
 * layers the viewer's standing on top to decide if THIS viewer is locked out.
 */
export function isAccessGated(accessLevel?: string): boolean {
  return accessLevel != null && !UNGATED_ACCESS_LEVELS.has(accessLevel);
}

/**
 * Widen a clip's raw `accessLevel` string into the extended access space used by
 * {@link isPopStoreExtendedUserAccessPermitted}. `undefined` (unrestricted / the
 * `all` token) means the clip gates no one.
 *
 * A clip can carry `accessLevel === 'follower'` — the FREE follower tier — which
 * is NOT a member of the content-side `PopStoreUserAccessLevel` enum, so the
 * generic `userAccessLevelToExtended` can't map it (returns `undefined` → reads
 * as unrestricted, never locks a follower clip). The extended enum's string
 * values equal the content tokens exactly, so map the raw string straight
 * through — including `follower`. Mirrors RN's `ACCESS_ORDER`.
 */
export function clipRequiredAccessLevel(
  accessLevel: string | undefined,
): PopStoreExtendedUserAccessLevel | undefined {
  if (accessLevel == null || accessLevel === PopStoreUserAccessLevel.ALL) {
    return undefined;
  }
  return accessLevel as PopStoreExtendedUserAccessLevel;
}

/**
 * Whether a clip is locked FOR THIS VIEWER — the clip is gated AND the viewer's
 * synthesized standing (`extendedSubscriptionLevel` from `BioUserAccessProvider`,
 * `undefined` for a stranger) doesn't meet its required tier. Owner-bypass is the
 * caller's concern (the grid already exempts `editable` mode). Used both by the
 * in-player paywall and the grid thumbnail so a subscribed/following viewer sees
 * the unblurred, unlocked thumbnail.
 */
export function isClipLockedForViewer(
  accessLevel: string | undefined,
  viewerLevel: PopStoreExtendedSubscriptionLevel | undefined,
): boolean {
  return !isPopStoreExtendedUserAccessPermitted(
    clipRequiredAccessLevel(accessLevel),
    viewerLevel,
  );
}
